import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import LogoutModal from "./LogoutModal";
import RegisterModal from "./RegisterModal";

const NAV_EXPANDED_WIDTH = "16rem";
const NAV_COLLAPSED_WIDTH = "5rem";

const links = {
  "/testing": { name: "Integration Testing", icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" },
  "/device": { name: "Device Config", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" },
  "/debug": { name: "Debug", icon: "M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" },
  "/devicetesting": { name: "Testing Device", icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" },
  "/ota-update": { name: "OTA Update", icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" },
  "/water-quality-parameters": { name: "Water Quality", icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" },
  "/pcb-testing": { name: "PCB Testing", icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" },
  "/device-registration": { name: "Device Registration", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
  "/filter-confs": { name: "Add Filter Confs", icon: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" },
  "/add-customer": { name: "Add Customer", icon: "M12 9v3m0 0v3m0-3h3m-3 0h-3M12 5a4 4 0 100 8 4 4 0 000-8zm0 10c-3.313 0-6 1.343-6 3v2h12v-2c0-1.657-2.687-3-6-3z" },
};

export default function Navbar() {
  const [showRegister, setShowRegister] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("navbarCollapsed") === "true";
    } catch {
      return false;
    }
  });
  const { logout, loggedIn } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.setItem("navbarCollapsed", String(isCollapsed));
    } catch {
      // ignore storage errors
    }
  }, [isCollapsed]);

  function toggleCollapsed() {
    setIsCollapsed((prev) => !prev);
  }

  function gotoRoute(route) {
    if (route === location.pathname && route === "/filter-confs") {
      window.dispatchEvent(new Event("filterConfs:refresh"));
    } else {
      navigate(route);
    }
  }

  return (
    <aside
      className={`flex-shrink-0 flex flex-col h-screen py-6 px-4 bg-white shadow overflow-hidden border-r relative z-50 ${
        isCollapsed ? "items-center" : ""
      }`}
      style={{
        width: isCollapsed ? NAV_COLLAPSED_WIDTH : NAV_EXPANDED_WIDTH,
        minWidth: isCollapsed ? NAV_COLLAPSED_WIDTH : NAV_EXPANDED_WIDTH,
        maxWidth: isCollapsed ? NAV_COLLAPSED_WIDTH : NAV_EXPANDED_WIDTH,
      }}
    >
      
      <div className={`flex items-center mb-8 w-full ${isCollapsed ? "justify-center" : "justify-between px-2"}`}>
        {!isCollapsed && <span className="text-2xl font-black text-indigo-600 tracking-wide">Gauge</span>}
        <button
          type="button"
          onClick={toggleCollapsed}
          className="p-2 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 focus:outline-none transition-colors"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {loggedIn && (
        <div className="flex flex-col gap-2 flex-grow overflow-y-auto w-full no-scrollbar">
          {Object.keys(links).map((link) => {
            const isActive = location.pathname === link;
            const { name, icon } = links[link];
            return (
              <button
                key={link}
                title={name}
                className={`flex items-center gap-3 text-left p-3 rounded-lg transition-colors group ${
                  isActive 
                    ? "bg-indigo-50 text-indigo-700 font-semibold" 
                    : "text-gray-600 hover:bg-gray-50 hover:text-indigo-500"
                } ${isCollapsed ? "justify-center" : "px-4"}`}
                onClick={() => gotoRoute(link)}
              >
                <svg className={`w-6 h-6 flex-shrink-0 ${isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                </svg>
                {!isCollapsed && <span className="truncate">{name}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className={`flex flex-col gap-3 mt-auto pt-4 border-t border-gray-100 ${isCollapsed ? "w-full items-center" : "w-full"}`}>
        <button
          className={`flex justify-center items-center gap-2 text-indigo-600 font-semibold border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors ${isCollapsed ? "p-3 w-12 h-12" : "py-2 px-4 w-full"}`}
          onClick={() => setShowRegister(true)}
          title="Register"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
          {!isCollapsed && <span>Register</span>}
        </button>
        {loggedIn && (
          <button
            className={`flex justify-center items-center gap-2 text-red-600 font-semibold border border-red-200 rounded-lg hover:bg-red-50 transition-colors ${isCollapsed ? "p-3 w-12 h-12" : "py-2 px-4 w-full"}`}
            onClick={() => setShowLogout(true)}
            title="Logout"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            {!isCollapsed && <span>Logout</span>}
          </button>
        )}
        <RegisterModal open={showRegister} onClose={() => setShowRegister(false)} />
        <LogoutModal
          open={showLogout}
          onClose={() => setShowLogout(false)}
          onConfirm={() => { setShowLogout(false); logout(); }}
        />
      </div>
    </aside>
  );
}
